import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { mapTemplatePropsToStyles } from '../../utility/template';
import { DEFAULT_TEXT_TRANSCRIPT_CONFIG } from '../../videoTranscriptionProcessor/constants/karaokeConstants';

import type { Preset } from '../textProcessor/TextBrandingService';

// Coerce potential preview text to a trimmed string
function toPreviewSafeText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

// Split text into first word + remainder
function splitFirstWord(text: string): { first?: string; rest: string } {
  if (!text) return { rest: '' };
  const match = text.match(/^(\S+)([\s\S]*)$/);
  if (!match) return { first: text, rest: '' };
  return { first: match[1], rest: match[2].trimStart() };
}

// Derive highlight color from template: only when transcriptEffect is set.
function coerceTranscriptColor(
  value: Preset['transcriptColor'],
  fallback: string,
) {
  if (!value) return fallback;
  if (typeof value === 'string') return value; // already rgba string
  if (typeof value === 'object' && 'hex' in value) {
    // DatoColor shape
    return value.hex || fallback;
  }
  return fallback;
}

function getHighlightColor(
  template: Preset,
  fallback = DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color,
): string {
  if (!template.transcriptEffect) return '';
  return coerceTranscriptColor(template.transcriptColor, fallback);
}

interface PresetPreviewTextProps {
  template: Preset;
}

export default function PresetPreviewText({
  template,
}: PresetPreviewTextProps) {
  // Derive preview strings + highlight color via helpers
  const previewSafeText = toPreviewSafeText(
    template.previewText || template.title,
  );
  const highlightColor = getHighlightColor(
    template,
    DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color,
  );
  const { first: firstWord, rest: restText } = highlightColor
    ? splitFirstWord(previewSafeText)
    : { first: undefined, rest: '' };
  const dynamicStyles = useMemo(() => {
    if (!template) {
      return {};
    }
    const result = mapTemplatePropsToStyles(template);
    if (template.transcriptEffect === 'highlight' && result.padding) {
      if (firstWord && !restText) {
        result.padding = '1px 0px 1px 0';
      } else {
        result.padding = '1px 6px 1px 0';
      }
    }
    return result;
  }, [template, firstWord, restText]);

  return (
    <TemplatePreviewText
      aria-label={`Apply ${template.title} template`}
      fontFamily={
        typeof template.fontFamily === 'string'
          ? template.fontFamily
          : undefined
      }
      fontWeight={
        typeof template.fontWeight === 'number'
          ? template.fontWeight
          : undefined
      }
      uppercase={Boolean(template.uppercase)}
      dynamic={dynamicStyles}
    >
      {firstWord && highlightColor ? (
        <>
          <TemplatePreviewTextHighlight
            color={highlightColor}
            enlargeEffect={template.transcriptEffect === 'enlarge'}
            highlightEffect={template.transcriptEffect === 'highlight'}
          >
            {firstWord}
          </TemplatePreviewTextHighlight>
          {restText ? ` ${restText}` : ''}
        </>
      ) : (
        previewSafeText
      )}
    </TemplatePreviewText>
  );
}

const TemplatePreviewText = styled.div<{
  fontFamily?: string;
  fontWeight?: number;
  uppercase?: boolean;
  dynamic: Record<string, string | number | boolean | undefined>;
}>`
  color: #fff;
  font-size: 12px;
  line-height: normal;
  text-align: center;
  font-family: ${p => p.fontFamily || 'Inter'};
  font-weight: ${p => p.fontWeight || 400};
  text-transform: ${p => (p.uppercase ? 'uppercase' : 'none')};
  ${p =>
    Object.entries(p.dynamic || {})
      .map(([k, v]) => `${k}: ${v};`)
      .join('\n')}
`;

const TemplatePreviewTextHighlight = styled.span<{
  color: string;
  enlargeEffect: boolean;
  highlightEffect: boolean;
}>`
  ${p =>
    p.highlightEffect
      ? css`
          background: ${p.color};
          padding: 0px 3px;
          border: 1px solid ${p.color};
        `
      : css`
          color: ${p.color};
        `}
  ${p => (p.enlargeEffect ? 'font-size: 133.3%;' : '')}
`;
